import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import { BookLock } from "lucide-react";
import type { PiLedger } from "@hisense/shared";
import { useCanSeeRedPi } from "@/app/access";
import { setLedger, useLedger } from "@/app/ledger";
import { PreviewGate } from "@/components/shell/FeaturePreviewGate";
import { Button } from "@/components/ui/Button";

const OPTIONS: readonly PiLedger[] = ["normal", "red"];

/**
 * Công tắc sổ PI ở thanh trên (Red PI, Roary 04/11/2026, mục 9) — cạnh công tắc công ty, cùng cách làm: đổi sổ là xoá dữ liệu đệm rồi
 * tải lại, không bao giờ hiện lẫn hai sổ; khoá trong lúc tạo / sửa. Chỉ người có ô Xem Red PI thấy công tắc.
 */
export function LedgerSwitch() {
  const { t } = useTranslation();
  const canSee = useCanSeeRedPi();
  const ledger = useLedger();
  const cache = useQueryClient();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const editing = /\/(new|edit)\/?$/.test(pathname);
  if (!canSee) return null;
  return (
    <div role="group" aria-label={t("redPi.label")} title={t(editing ? "redPi.lockedEditing" : "redPi.hint")} className="flex items-center gap-1.5">
      <BookLock className="hidden size-4 text-ink-faint sm:block" aria-hidden="true" />
      <div className="flex items-center rounded-sm border border-line bg-surface p-0.5">
        {OPTIONS.map((x) => (
          <button
            key={x}
            type="button"
            aria-pressed={ledger === x}
            disabled={editing && ledger !== x}
            onClick={() => {
              if (ledger === x || editing) return;
              setLedger(x);
              void cache.resetQueries();
              // Trang Red PI luôn chạy trong sổ Red (mở trang là vào sổ Red): về sổ thường thì rời trang đó sang danh sách PI thường.
              if (x === "normal" && pathname.startsWith("/red-pi")) navigate("/proforma-invoices");
            }}
            className={
              "rounded-[3px] px-2 py-1 text-xs font-medium transition-colors " +
              (ledger === x
                ? x === "red" ? "bg-danger text-white" : "bg-brand text-brand-on"
                : "text-ink-faint hover:text-ink-soft disabled:cursor-not-allowed disabled:opacity-50")
            }
          >
            {t(x === "red" ? "redPi.red" : "redPi.normal")}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * Dải nhắc trên mọi trang khi đang ở sổ Red — không ai lỡ lập chứng từ hay đọc báo cáo của sổ Red mà tưởng là sổ thường. Sổ Red còn
 * khoá "Đang làm": chưa mở khoá (hoặc hết 30 phút) thì ô mật khẩu hiện ngay trong dải.
 */
export function RedLedgerBanner() {
  const { t } = useTranslation();
  const canSee = useCanSeeRedPi();
  const ledger = useLedger();
  const cache = useQueryClient();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  if (!canSee || ledger !== "red") return null;
  const editing = /\/(new|edit)\/?$/.test(pathname);
  return (
    <div role="status" className="m-4 mb-0 space-y-2 rounded-md border border-danger/40 bg-danger-tint p-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="flex items-center gap-2 text-sm font-semibold text-danger">
            <BookLock className="size-4" aria-hidden="true" />
            {t("redPi.banner")}
          </p>
          <p className="mt-0.5 text-xs text-ink-soft">{t("redPi.bannerHint")}</p>
        </div>
        <Button
          size="sm"
          variant="secondary"
          disabled={editing}
          title={editing ? t("redPi.lockedEditing") : undefined}
          onClick={() => {
            setLedger("normal");
            void cache.resetQueries();
            if (pathname.startsWith("/red-pi")) navigate("/proforma-invoices");
          }}
        >
          {t("redPi.backToNormal")}
        </Button>
      </div>
      {/* Trang Red PI có cổng riêng (FeaturePreviewGate theo trang); ở các trang khác thì mở khoá ngay tại dải này. */}
      {!pathname.startsWith("/red-pi") && <PreviewGate feature="redPi" inline>{null}</PreviewGate>}
    </div>
  );
}
